/**
 * Validates a user's SQL query result against the solution result.
 * 
 * Strategy:
 * 1. Compare Row Count (Exact match required)
 * 2. Compare Column Count (Exact match required)
 * 3. Compare Column Names (Strict mode: exact match)
 * 4. Compare Data Values (Cell-by-cell)
 * 
 * @param {Array} userResult - Array of objects representing user query result
 * @param {Array} solutionResult - Array of objects representing solution query result
 * @param {Object} options - Validation options
 * @param {boolean} options.checkColumnNames - Whether to enforce exact column name matches (default: true)
 * @param {boolean} options.checkOrder - Whether to enforce exact row order (default: false - we sort both before comparing unless solution has explicit ORDER BY)
 * 
 * @returns {Object} result
 * @returns {boolean} result.passed - True if results match
 * @returns {string} result.message - User-facing status message
 * @returns {Object} result.details - Detailed mismatch info (optional)
 */
export function validateSQLResult(userResult, solutionResult, options = {}) {
    const {
        checkColumnNames = true,
        checkOrder = false,
        userQuery = "",
        solutionQuery = ""
    } = options;

    /**
     * HEURISTIC DETECTIVES
     * Analyze why the query might have failed
     */
    const runDetectives = (failureType, context = {}) => {
        const uQ = userQuery.toUpperCase();
        const sQ = solutionQuery.toUpperCase();

        // 1. LIMIT Detective
        if (failureType === 'row_count' && userResult.length > solutionResult.length) {
            if (sQ.includes('LIMIT') && !uQ.includes('LIMIT')) {
                return `Too many rows returned (${userResult.length} vs ${solutionResult.length}). Did you forget to add a LIMIT clause?`;
            }
        }

        // 2. Filter Detective (0 rows)
        if (failureType === 'row_count' && userResult.length === 0 && solutionResult.length > 0) {
            if (uQ.includes('WHERE')) {
                return "Your query returned no results. Check your WHERE clause - you might be filtering out everything (check for typos in text values like 'USA' vs 'usa').";
            }
        }

        // 3. Sort Direction Detective
        if (failureType === 'value_mismatch') {
            // Check if one uses DESC and other implies ASC
            const userHasDesc = uQ.includes('DESC');
            const solHasDesc = sQ.includes('DESC');

            if (solHasDesc && !userHasDesc) {
                return "The values don't match. The solution filters for the 'newest' or 'top' items (DESC), but your query seems to use the default order (ASC). Try adding DESC to your ORDER BY.";
            }
            if (!solHasDesc && userHasDesc) {
                return "The values don't match. It looks like you're sorting in DESC order, but the question asks for ascending (oldest/smallest first).";
            }
        }

        // 4. Column Name Detective
        if (failureType === 'column_mismatch') {
            return `Column count mismatch. You have ${context.userCols} columns but expected ${context.solCols}. Check your SELECT list.`;
        }

        return null;
    };

    // 1. Check for empty/null results
    if (!userResult || userResult.length === 0) {
        if (!solutionResult || solutionResult.length === 0) {
            return { passed: true, message: "Correct! Both queries returned no results." };
        }
        const hint = runDetectives('row_count');
        return {
            passed: false,
            message: hint || "Your query returned no results, but the solution expected data."
        };
    }

    if (!solutionResult || solutionResult.length === 0) {
        return { passed: false, message: "Your query returned results, but the solution expected none." };
    }

    // 2. Check Row Count
    if (userResult.length !== solutionResult.length) {
        const hint = runDetectives('row_count');
        return {
            passed: false,
            message: hint || `Row count mismatch. Your query returned ${userResult.length} rows, expected ${solutionResult.length}.`
        };
    }

    // Normalize results for comparison
    const userRows = [...userResult];
    const solutionRows = [...solutionResult];

    const userColumns = Object.keys(userRows[0]);
    const solutionColumns = Object.keys(solutionRows[0]);

    // 3. Check Column Count
    if (userColumns.length !== solutionColumns.length) {
        const hint = runDetectives('column_mismatch', { userCols: userColumns.length, solCols: solutionColumns.length });
        return {
            passed: false,
            message: hint || `Column count mismatch. Your query returned ${userColumns.length} columns, expected ${solutionColumns.length}.`
        };
    }

    // 4. Check Column Names (Strict Mode)
    if (checkColumnNames) {
        const missingColumns = solutionColumns.filter(
            solCol => !userColumns.some(userCol => userCol.toLowerCase() === solCol.toLowerCase())
        );

        if (missingColumns.length > 0) {
            return {
                passed: false,
                message: `Missing column(s): ${missingColumns.join(', ')}. Check your aliases (e.g. SELECT count(*) AS local_count).`
            };
        }
    }

    // 5. Compare Data
    // If checkOrder is false, we sort for comparison stability
    if (!checkOrder) {
        const bigIntReplacer = (key, value) => typeof value === 'bigint' ? value.toString() : value;
        // Normalize row for sorting: sort keys alphabetically to handle column order differences (e.g. SELECT * variations)
        const normalizeForRowSort = (row) => {
            const sortedObj = {};
            Object.keys(row).sort().forEach(key => {
                sortedObj[key] = row[key];
            });
            return JSON.stringify(sortedObj, bigIntReplacer);
        };

        const genericSort = (a, b) => normalizeForRowSort(a).localeCompare(normalizeForRowSort(b));
        userRows.sort(genericSort);
        solutionRows.sort(genericSort);
    }

    // Row-by-Row Comparison
    for (let i = 0; i < solutionRows.length; i++) {
        const solRow = solutionRows[i];
        const userRow = userRows[i];

        // Check each column
        for (let colIndex = 0; colIndex < solutionColumns.length; colIndex++) {
            const col = solutionColumns[colIndex];
            // Find corresponding user column (handle case insensitivity)
            let userColName = userColumns.find(k => k.toLowerCase() === col.toLowerCase());

            // Fallback to positional match if strict name check is disabled and name match failed
            if (!userColName && !checkColumnNames) {
                userColName = userColumns[colIndex];
            }

            const solVal = solRow[col];
            const userVal = userRow[userColName];

            if (!valuesMatch(solVal, userVal)) {

                // Detailed Value Checks
                if (solVal === null && userVal !== null) {
                    return { passed: false, message: `Row ${i + 1}: Expected NULL in column '${col}', but got '${userVal}'.` };
                }
                if (typeof solVal === 'number' && typeof userVal === 'string') {
                    return { passed: false, message: `Row ${i + 1}: Data type mismatch in column '${col}'. Expected a number, got text.` };
                }

                const hint = runDetectives('value_mismatch');
                return {
                    passed: false,
                    message: hint || `Value mismatch at Row ${i + 1}, Column '${col}'. Expected '${solVal}', got '${userVal}'.`
                };
            }
        }
    }

    return { passed: true, message: "Correct! Your result matches the solution perfectly." };
}

/**
 * Helper to compare two SQL values loosely (handles types)
 */
function valuesMatch(a, b) {
    // exact equality
    if (a === b) return true;

    // null handling
    if (a === null && b === null) return true;
    if (a === null || b === null) return false;

    // numeric handling (string '100' vs number 100)
    // DuckDB might return BigInt for counts
    if (typeof a === 'bigint') a = Number(a);
    if (typeof b === 'bigint') b = Number(b);

    if (typeof a === 'number' && typeof b === 'number') {
        // Float precision tolerance
        return Math.abs(a - b) < 0.0001;
    }

    // String normalization
    return String(a).trim() === String(b).trim();
}
